import { useMemo, useState } from "react";
import { Download, Layers, GraduationCap, School, UserCheck, FileSpreadsheet } from "lucide-react";

const selectCls = "w-full min-w-0 rounded-full border border-[#E5DCCB] bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B3A2B]/30";
const btnCls = "shrink-0 inline-flex items-center justify-center gap-2 rounded-full bg-[#1C1917] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#8B3A2B] transition-colors disabled:opacity-40";

const Picker = ({ icon: Icon, label, options, onPick, busy, testid }) => {
  const [value, setValue] = useState("");
  const chosen = options.find((o) => o.value === value);
  return (
    <div className="flex flex-col gap-2" data-testid={`${testid}-panel`}>
      <span className="flex items-center gap-2 text-sm font-semibold"><Icon className="w-4 h-4 text-[#8B3A2B]" /> {label}</span>
      <div className="flex gap-2">
        <select value={value} onChange={(e) => setValue(e.target.value)} className={selectCls} data-testid={`${testid}-select`}>
          <option value="">— اختر —</option>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <button disabled={!chosen || busy} onClick={() => onPick(chosen)} className={btnCls} data-testid={`${testid}-btn`} aria-label={label}>
          <Download className="w-4 h-4" /> <span className="hidden sm:inline">تنزيل</span>
        </button>
      </div>
    </div>
  );
};

// Admin panel: download sign-in cards for every class, one teacher's classes, or one school's students.
export const BulkCards = ({ users, busy, onPick }) => {
  const teachers = useMemo(() => users.filter((u) => u.role === "teacher").map((u) => ({ value: u.id, label: u.name })), [users]);
  const schools = useMemo(() => [...new Set(users.filter((u) => u.role === "user" && u.school).map((u) => u.school))]
    .sort((a, b) => a.localeCompare(b, "ar")).map((s) => ({ value: s, label: s })), [users]);
  const allSchools = useMemo(() => [...new Set(users.filter((u) => u.role !== "admin" && u.school).map((u) => u.school))]
    .sort((a, b) => a.localeCompare(b, "ar")).map((s) => ({ value: s, label: s })), [users]);
  return (
    <section className="bg-white border border-[#E5DCCB] p-5 sm:p-6 mb-6 grid gap-5 lg:grid-cols-2 xl:grid-cols-3 items-end" data-testid="admin-bulk-cards">
      <div className="flex flex-col gap-2">
        <span className="flex items-center gap-2 text-sm font-semibold"><Layers className="w-4 h-4 text-[#8B3A2B]" /> بطاقات الدخول</span>
        <button disabled={busy} onClick={() => onPick("all", "", "كل الصفوف")} className={btnCls} data-testid="admin-all-cards-btn">
          <Download className="w-4 h-4" /> تنزيل بطاقات كل الصفوف
        </button>
      </div>
      <Picker icon={GraduationCap} label="بطاقات معلم" options={teachers} busy={busy} testid="admin-teacher-cards"
        onPick={(o) => onPick("teacher", o.value, `المعلم ${o.label}`)} />
      <Picker icon={School} label="بطاقات مدرسة (ملف مضغوط، ملف لكل صف)" options={schools} busy={busy} testid="admin-school-cards"
        onPick={(o) => onPick("school", o.value, o.label)} />
      <Picker icon={UserCheck} label="بطاقات معلمي مدرسة" options={allSchools} busy={busy} testid="admin-school-teachers-cards"
        onPick={(o) => onPick("teachers", o.value, `معلمو ${o.label}`)} />
      <Picker icon={FileSpreadsheet} label="قائمة Excel بأسماء الدخول لمدرسة" options={schools} busy={busy} testid="admin-school-excel"
        onPick={(o) => onPick("excel", o.value, `قائمة ${o.label}`)} />
    </section>
  );
};
